import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { collectionAPI } from '../../api/waybillAPI';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const BUCKET_STYLES = {
  current:  'bg-teal-100 text-teal-700',
  '1_30':   'bg-yellow-100 text-yellow-700',
  '31_60':  'bg-orange-100 text-orange-700',
  '61_90':  'bg-red-100 text-red-600',
  '90_plus':'bg-red-200 text-red-800',
};
const BUCKET_LABELS = {
  current:  'Current',
  '1_30':   '1–30 days',
  '31_60':  '31–60 days',
  '61_90':  '61–90 days',
  '90_plus':'90+ days',
};
const PRINT_BUCKET_CLASS = {
  current:  'bucket-current',
  '1_30':   'bucket-1_30',
  '31_60':  'bucket-31_60',
  '61_90':  'bucket-61_90',
  '90_plus':'bucket-90_plus',
};

const STATUS_COLORS = {
  draft:          'bg-gray-100 text-gray-600',
  sent:           'bg-blue-100 text-blue-700',
  paid:           'bg-green-100 text-green-700',
  partially_paid: 'bg-yellow-100 text-yellow-700',
  overdue:        'bg-red-100 text-red-700',
  voided:         'bg-red-100 text-red-600 line-through',
};

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
      {status ? status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1) : 'Draft'}
    </span>
  );
}

function BucketBadge({ bucket }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${BUCKET_STYLES[bucket] || BUCKET_STYLES.current}`}>
      {BUCKET_LABELS[bucket] || bucket}
    </span>
  );
}

export default function AgeingReportPage() {
  const navigate   = useNavigate();
  const todayStr   = new Date().toISOString().split('T')[0];

  const [asOfDate, setAsOfDate] = useState(todayStr);
  const [billType, setBillType] = useState('');
  const [data, setData]         = useState({ data: [], summary: {}, grandTotal: 0 });
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);

  const loadReport = async (aod = asOfDate, bt = billType) => {
    setLoading(true);
    try {
      const res = await collectionAPI.getAgeing({ asOfDate: aod, billType: bt });
      setData(res.data || { data: [], summary: {}, grandTotal: 0 });
      setLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, []); // eslint-disable-line

  const handlePrint = () => window.print();

  const bills     = data.data      || [];
  const summary   = data.summary   || {};
  const grandTotal = data.grandTotal || 0;

  const fmtDateLong = (ds) =>
    new Date(ds + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

  const bucketCards = [
    { key: 'current',  label: 'Current (not yet due)', color: 'border-teal-500',   textColor: 'text-teal-600' },
    { key: 'd1_30',    label: '1–30 days overdue',     color: 'border-yellow-400', textColor: 'text-yellow-600' },
    { key: 'd31_60',   label: '31–60 days overdue',    color: 'border-orange-400', textColor: 'text-orange-600' },
    { key: 'd61_90',   label: '61–90 days overdue',    color: 'border-red-400',    textColor: 'text-red-500' },
    { key: 'd90_plus', label: '90+ days overdue',      color: 'border-red-700',    textColor: 'text-red-700' },
  ];

  return (
    <Layout pageTitle="AR Ageing Report">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-header { display: block !important; }
          @page { size: A4 landscape; margin: 8mm; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #aaa; padding: 3px 5px; font-size: 8pt; }
          .bucket-current { background: #ccfbf1; }
          .bucket-1_30    { background: #fef9c3; }
          .bucket-31_60   { background: #ffedd5; }
          .bucket-61_90   { background: #fee2e2; }
          .bucket-90_plus { background: #fecaca; }
          body { font-size: 9pt; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Print-only header */}
      <div className="print-header" style={{ textAlign: 'center', marginBottom: 8 }}>
        <h1 style={{ fontSize: '14pt', margin: 0 }}>AR AGEING REPORT</h1>
        <p style={{ margin: '2px 0', fontSize: '10pt' }}>As of: {loaded ? fmtDateLong(asOfDate) : asOfDate}</p>
        <p style={{ margin: '2px 0', fontSize: '10pt' }}>Total Outstanding: ₱{PHP(grandTotal)}</p>
      </div>

      <div className="space-y-4 max-w-7xl">
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg shadow px-4 py-3 no-print">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">As of Date</label>
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Bill Type</label>
            <select value={billType} onChange={(e) => setBillType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">All</option>
              <option value="1">FF Billing</option>
              <option value="2">Trucking Billing</option>
            </select>
          </div>
          <button onClick={() => loadReport()} disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Loading…' : 'Load'}
          </button>
          {loaded && bills.length > 0 && (
            <button onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
              Print
            </button>
          )}
        </div>

        {/* Bucket summary cards */}
        {loaded && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {bucketCards.map(({ key, label, color, textColor }) => {
              const bucket = summary[key] || { bills: [], total: 0 };
              return (
                <div key={key} className={`bg-white rounded-lg shadow p-4 text-center border-t-4 ${color}`}>
                  <p className="text-xs text-gray-500 uppercase mb-1">{label}</p>
                  <p className={`text-xl font-semibold ${textColor}`}>₱{PHP(bucket.total)}</p>
                  <p className="text-xs text-gray-400">{(bucket.bills || []).length} bill(s)</p>
                </div>
              );
            })}
            <div className="bg-gray-800 rounded-lg shadow p-4 text-center text-white">
              <p className="text-xs uppercase mb-1 text-gray-300">Total Outstanding</p>
              <p className="text-xl font-semibold">₱{PHP(grandTotal)}</p>
              <p className="text-xs text-gray-400">{bills.length} bill(s)</p>
            </div>
          </div>
        )}

        {/* Table */}
        {loaded && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Bill No.', 'Customer', 'Waybill', 'Bill Date', 'Due Date',
                    'Amount Due', 'Collected', 'Outstanding', 'Days Overdue', 'Bucket', 'Status', ''].map((h) => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bills.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400">No outstanding bills found.</td></tr>
                ) : (
                  bills.map((b) => {
                    const bucketClass = PRINT_BUCKET_CLASS[b.bucket] || '';
                    return (
                      <tr key={b._id} className={`hover:bg-gray-50 ${bucketClass}`}>
                        <td className="px-3 py-2 font-medium text-teal-700">{b.BillName}</td>
                        <td className="px-3 py-2">{b.customerName}</td>
                        <td className="px-3 py-2">{b.waybillSerial}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(b.BillDate)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(b.BillDueDate)}</td>
                        <td className="px-3 py-2 text-right">₱{PHP(b.amountDue)}</td>
                        <td className="px-3 py-2 text-right text-green-600">₱{PHP(b.totalCollected || 0)}</td>
                        <td className="px-3 py-2 text-right font-medium text-red-600">₱{PHP(b.outstanding)}</td>
                        <td className="px-3 py-2 text-center">
                          {b.daysOverdue > 0
                            ? <span className="text-red-600 font-medium">{b.daysOverdue} days</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2"><BucketBadge bucket={b.bucket} /></td>
                        <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => navigate(
                              b.BillTypeId === 2
                                ? `/billing/trucking-billing/${b._id}/edit`
                                : `/billing/ff-billing/${b._id}/edit`
                            )}
                            className="p-1 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                            title="Edit bill"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
                {bills.length > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={7} className="px-3 py-2 text-right text-gray-600">Grand Total Outstanding</td>
                    <td className="px-3 py-2 text-right text-red-700">₱{PHP(grandTotal)}</td>
                    <td colSpan={4} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
